// LUMINA · pages/vajra/VajraLanding.jsx
// Phase 99: 首页简化 — Hero增强 + 精选轮播 + 法讯日历 + 往期回顾 · 13区块→6区块

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API, GD, GDL, GDH, BG, TXT, TXT2, EVENT_TYPE_LABEL, EVENT_TYPE_COLOR, TZ_LABELS } from './constants';
import EventDetailModal from './components/EventDetailModal';
import PublicEventCalendar from './components/PublicEventCalendar';
import FeaturedCarousel from './components/FeaturedCarousel';
import MeritBoard from './components/MeritBoard';

const GOOGLE_CLIENT_ID = "213851968461-nm8dc9ut7no26lpkb7f3ha4pkmu2ketj.apps.googleusercontent.com";

// T16: FAQ questions (bilingual)
const FAQS = [
{q:"如何加入如意宝金刚社群？",
a:"点击「申请加入」，填写邮箱和密码即可自助注册。注册后可浏览法讯、报名法会，并与传承上师接触。部分高级法教需要相应灌顶资格。",
q_en:"How do I join the BTF Canada Vajra Community?",
a_en:"Click 'Apply', enter your email and password to self-register. After registering you can browse events, register for teachings, and connect with lineage teachers. Some advanced teachings require empowerment credentials.",
},{q:"参加法会需要什么准备？",
a:"公开法会欢迎任何有信心的行者参加。灌顶法会一般需要皈依三宝，具体资格请见各法会说明。建议提前15分钟到场（线下）或测试网络（线上）。",
q_en:"What do I need to attend a puja or teaching?",
a_en:"Public events welcome any sincere practitioner. Empowerment ceremonies generally require taking refuge; specific requirements are listed in each event description. Arrive 15 minutes early (in-person) or test your connection beforehand (online).",
},{q:"供养如何使用？",
a:"所有供养由显密文教基金会（BTF CANADA）管理，按上师弘法、场地建设、法本流通、助学四大用途分配。",
q_en:"How are offerings used?",
a_en:"All offerings are managed by BTF CANADA and distributed across four purposes: teacher support, venue and operations, dharma text printing and distribution, and student scholarships.",
},{q:"如何获得灌顶资格？",
a:"请先完成皈依三宝，并参加相关前行课程。灌顶资格由具格上师认定，具体要求请联系社群管理团队。建议循序渐进，不急于求成。",
q_en:"How do I obtain empowerment credentials?",
a_en:"First take refuge in the Three Jewels and attend relevant preparatory courses. Empowerment credentials are confirmed by a qualified teacher. Please contact the community team for specific requirements, and proceed gradually without rushing.",
},{q:"我是初学者，从哪里开始？",
a:"建议从「修行次第」选择有缘传承，或直接参加公开法讯感受共修氛围。教法图书馆有专为初学者整理的入门资料。",
q_en:"I'm a beginner — where do I start?",
a_en:"We recommend exploring the Dharma Path to find a lineage connection, or attending a public event to experience group practice. The Dharma Library has introductory materials curated for beginners.",
},
];

// ── DZOKDEN 四业纯色底板 (v3: 抛弃白色渐变，全色系纯色底) ──
// 审计结论: #FFFFFF→淡色 渐变让卡片90%面积是白色，在羊皮纸上完全看不出来
// 修复: 全部改为纯色底，同色系微渐变保持光泽但不含白色
const KARMA_CARD_STYLES = {
  pacifying: {
    label: { zh: "息业 · 净琉璃", en: "Pacifying · Healing" },
    bg: "linear-gradient(135deg, #D6E8FF 0%, #C5DCFF 100%)",
    border: "rgba(0,113,227,0.35)",
    borderHover: "#0071E3",
    shadow: "0 4px 20px rgba(0,113,227,0.25)",
    indicator: "#0071E3",
    badgeBg: "rgba(0,113,227,0.12)",
    badgeColor: "#0052CC",
    badgeBorder: "rgba(0,113,227,0.4)",
    accent: "#003D99",
    deities: "药师佛 · 白度母 · 般若佛母",
    desc_zh: "息灭病障、平息违缘、净化业力。如琉璃光遍照，令身心安泰。",
    desc_en: "Pacifying sickness, obstacles, and negative karma — like lapis lazuli light pervading all.",
  },
  increasing: {
    label: { zh: "增业 · 宝生金", en: "Increasing · Enriching" },
    bg: "linear-gradient(135deg, #FFF0B3 0%, #FFE580 100%)",
    border: "rgba(200,160,0,0.4)",
    borderHover: "#C8A000",
    shadow: "0 4px 20px rgba(200,160,0,0.25)",
    indicator: "#C8A000",
    badgeBg: "rgba(200,160,0,0.12)",
    badgeColor: "#8B6914",
    badgeBorder: "rgba(200,160,0,0.45)",
    accent: "#6B4F10",
    deities: "黄财神 · 黑财神 · 地藏菩萨 · 绿度母",
    desc_zh: "增长福德资粮、寿命智慧、世间出世间一切善法。如大地承载万物。",
    desc_en: "Increasing merit, longevity, and all virtuous dharmas — like the earth supporting all beings.",
  },
  magnetizing: {
    label: { zh: "怀业 · 红莲华", en: "Magnetizing · Transforming" },
    bg: "linear-gradient(135deg, #FFDED9 0%, #FFC9C2 100%)",
    border: "rgba(220,60,60,0.35)",
    borderHover: "#DC3C3C",
    shadow: "0 4px 20px rgba(220,60,60,0.25)",
    indicator: "#DC3C3C",
    badgeBg: "rgba(220,60,60,0.12)",
    badgeColor: "#B81D1D",
    badgeBorder: "rgba(220,60,60,0.4)",
    accent: "#8B0000",
    deities: "作明佛母 · 大自在天 · 金刚亥母",
    desc_zh: "怀摄人缘、调伏烦恼、转化欲望为道用。如红莲华出淤泥而不染。",
    desc_en: "Magnetizing connections, transforming desire into the path — like a lotus rising unstained.",
  },
  subduing: {
    label: { zh: "诛业 · 金刚青", en: "Subduing · Cutting Through" },
    bg: "linear-gradient(135deg, #CCF5ED 0%, #B3EDE0 100%)",
    border: "rgba(13,148,148,0.4)",
    borderHover: "#0D9494",
    shadow: "0 4px 20px rgba(13,148,148,0.25)",
    indicator: "#0D9494",
    badgeBg: "rgba(13,148,148,0.12)",
    badgeColor: "#006D75",
    badgeBorder: "rgba(13,148,148,0.4)",
    accent: "#00474F",
    deities: "普巴金刚 · 大威德金刚 · 玛哈嘎拉",
    desc_zh: "降伏内外障碍、斩断无明、摧破魔障。如金刚霹雳，无坚不摧。",
    desc_en: "Subduing inner and outer obstacles, slashing through ignorance — indestructible as vajra thunder.",
  },
  default: {
    label: { zh: "密法", en: "Vajrayana" },
    bg: "linear-gradient(135deg, #FFE8D8 0%, #FFD4B8 100%)",
    border: "rgba(170,80,20,0.35)",
    borderHover: "rgba(170,80,20,0.55)",
    shadow: "0 4px 20px rgba(170,80,20,0.2)",
    indicator: "#AA5014",
    badgeBg: "rgba(170,80,20,0.12)",
    badgeColor: "#7C2D12",
    badgeBorder: "rgba(170,80,20,0.4)",
    accent: "#5C1A08",
    deities: "",
    desc_zh: "",
    desc_en: "",
  },
};

// ── 本尊名→四业推断 (身色映射) ──
function getEventKarmaStyle(ev) {
  // Priority 1: explicit karma_type field on event
  if (ev.karma_type && KARMA_CARD_STYLES[ev.karma_type]) {
    return KARMA_CARD_STYLES[ev.karma_type];
  }
  // Priority 2: infer from deity name in title/teacher fields
  const haystack = [
    ev.teacher_zh, ev.teacher_en, ev.title_zh, ev.title_en,
    ev.spiritual_function, ev.description,
  ].filter(Boolean).join(" ").toLowerCase();

  // 息业 — white/healing body: 白度母, 金刚萨埵, 药师佛, 多杰南炯(净化), 尊胜佛母
  if (/白度母|金刚萨埵|药师佛|普明|大日如来|尊胜佛母|二十一度母|多杰南炯|金刚萨垛|不动佛|white tara|vajrasattva|medicine buddha|ushnisha|vajravidarana/i.test(haystack)) {
    return KARMA_CARD_STYLES.pacifying;
  }
  // 增业 — yellow/gold/green body: 黄财神, 黑财神, 地藏, 绿度母, 财宝天王, 长寿佛
  if (/黄财神|黑财神|地藏|绿度母|财宝天王|增寿|长寿佛|增业|宝生|毗沙门|yellow dzambhala|black dzambhala|green tara|jambhala|vasudhara/i.test(haystack)) {
    return KARMA_CARD_STYLES.increasing;
  }
  // 怀业 — red body: 作明佛母, 金刚瑜伽母, 金刚亥母, 大自在天, 空行母
  if (/作明佛母|咕噜咕咧|大自在天|怀业|怀摄|金刚亥母|金刚瑜伽母|瑜伽母|红度母|空行母|莲师|kurukulle|vajravarahi|vajrayogini|dakini/i.test(haystack)) {
    return KARMA_CARD_STYLES.magnetizing;
  }
  // 诛业 — dark blue/black body: 大威德, 普巴金刚, 玛哈嘎拉, 吉祥天母, 黑文殊, 马头明王
  if (/普巴金刚|大威德|玛哈嘎拉|吉祥天母|黑文殊|金刚手|马头明王|狮面空行|忿怒|诛业|降伏|阎魔|vajrakilaya|yamantaka|mahakala|vajrapani|hayagriva|palden lhamo|shri devi/i.test(haystack)) {
    return KARMA_CARD_STYLES.subduing;
  }
  // 默认藏红色
  return KARMA_CARD_STYLES.default;
}

// T06: helper — days until an event
function getDaysUntil(dateStr) {
if (!dateStr) return null;
const d = new Date(dateStr);
if (isNaN(d)) return null;
const days = Math.ceil((d - new Date()) / 86400000);
return days > 0 ? days : null;
}

export default function VajraLanding({ onLogin, setPage, member, lang: langProp, T, setLang: setLangGlobal }) {
const navigate = useNavigate();
const [events,   setEvents]  = useState([]);
const [pujas,    setPujas]   = useState([]);
const [teachers, setTeachers]= useState([]);
const [selectedEvent, setSelectedEvent] = useState(null);
const [calMonth, setCalMonth]= useState(() => new Date());
// T15: event filtering
const [evFilter, setEvFilter]= useState({type:""});
const [evSearch, setEvSearch]= useState("");
// Phase 2: Upcoming Activities tab — "teaching" | "group_practice" | "normal"
const [evTab, setEvTab]      = useState("normal");
// T17: language — use global lang from VajraPortal; fall back to localStorage
const lang = langProp || localStorage.getItem("lumina_lang") || "zh";
// T16: FAQ
const [faqOpen, setFaqOpen]  = useState(null);
const [showAuthModal, setShowAuthModal] = useState(false);
const [importantEvents, setImportantEvents] = useState([]);
const [featuredRetreats, setFeaturedRetreats] = useState([]);
const [featuredSeries,   setFeaturedSeries]   = useState([]);
const [lightboxUrl,      setLightboxUrl]      = useState(null);  // full-size image
const [recommendedEvents, setRecommendedEvents] = useState([]);
const [pastExpanded, setPastExpanded] = useState(false);

const L = (zh, en) => lang === "zh" ? zh : en;
const location = useLocation();

useEffect(()=>{
fetch(`${API}/vajra/events/public?include_schedules=true`)
.then(r=>r.ok?r.json():[]).then(d=>setEvents(Array.isArray(d)?d:[])).catch(()=>{});
fetch(`${API}/vajra/puja`)
.then(r=>r.ok?r.json():[]).then(d=>setPujas(Array.isArray(d)?d.slice(0,6):[])).catch(()=>{});
fetch(`${API}/vajra/teachers?featured=true`)
.then(r=>r.ok?r.json():[]).then(d=>setTeachers(Array.isArray(d)?d:[])).catch(()=>{});
fetch(`${API}/vajra/events/public?is_featured=true`)
.then(r=>r.ok?r.json():[]).then(d=>{
  const today = new Date().toISOString().slice(0,10);
  const future = Array.isArray(d)?d.filter(e=>!e.start_date||e.start_date>=today):[];
  setFeaturedRetreats(future);  // show all featured, not capped at 4
}).catch(()=>{});
fetch(`${API}/vajra/events/public?category=important`)
.then(r=>r.ok?r.json():[]).then(d=>{
  const today = new Date().toISOString().slice(0,10);
  const active = Array.isArray(d)?d.filter(e=>!e.end_date||e.end_date>=today):[];
  setImportantEvents(active);
}).catch(()=>{});
fetch(`${API}/vajra/series/featured`)
.then(r=>r.ok?r.json():[]).then(d=>setFeaturedSeries(Array.isArray(d)?d:[])).catch(()=>{});
},[]);

// Phase 118: fetch energy-matched recommendations when member is logged in
useEffect(()=>{
  if (!member) return;
  const token = localStorage.getItem("vajra_token");
  if (!token) return;
  fetch(`${API}/vajra/my-dashboard`, {headers:{"Authorization":`Bearer ${token}`}})
    .then(r => r.ok ? r.json() : null)
    .then(d => d && setRecommendedEvents(d.recommended_events || []))
    .catch(()=>{});
}, [member]);

// Auto-open auth modal from ?auth=1 URL param (via useLocation for SPA nav)
useEffect(()=>{
  const params = new URLSearchParams(location.search);
  if (params.get("auth") === "1") setShowAuthModal(true);
}, [location.search]);

// Auto-navigate calendar to nearest upcoming event's month
useEffect(()=>{
if (allEvents.length===0) return;
const today = new Date().toISOString().slice(0,10);
const dates = allEvents.map(e=>e.start_date).filter(Boolean).sort();
const future = dates.filter(d => d >= today);
const target = future[0] || dates[dates.length-1];
if (target) setCalMonth(new Date(target));
}, [events, pujas]);

const allEvents = [
...pujas,
...events.filter(e=>!pujas.find(p=>p.id===e.id)),
];

// ── Image dedup for carousel — same-type events share default images,
//     so only the first event of each image gets a carousel slide.
//     Upload gallery images per event via Admin to give each its own slide.
const getEventImage = (ev) => {
  let g = ev.gallery_images;
  if (typeof g === 'string') { try { g = JSON.parse(g); } catch { g = null; } }
  if (Array.isArray(g) && g.length > 0) return { url: g[0], custom: true };
  if (ev.cover_image_url) return { url: ev.cover_image_url, custom: true };
  const DEFAULTS = { retreat: "/images/Tara2.jpg", empowerment: "/images/KalachakraMandalaLO-220801-190044.jpg", teaching: "/images/Naropa.jpg", puja: "/images/黄度母.jpg", weekly_practice: "/images/Naropa.jpg" };
  return { url: DEFAULTS[ev.event_type] || "/images/Tara2.jpg", custom: false };
};
// 去重: 仅自定义上传图参与（默认图不互斥—同类型多个法会各有自己的slide）
const seenCustomImgs = new Set();
const carouselEvents = featuredRetreats.filter(ev => {
  const { url: img, custom } = getEventImage(ev);
  if (custom && seenCustomImgs.has(img)) return false;  // only dedup custom images
  if (custom) seenCustomImgs.add(img);
  return true;
});

const featuredIds = new Set(carouselEvents.map(r => r.id));
const today = new Date().toISOString().slice(0,10);

// Filter events — including external ones (merged into main grid with 🌐 tag)
const allFiltered = allEvents.filter(ev => {
if (!evFilter.type && !evSearch && featuredIds.has(ev.id)) return false;
if (evSearch) {
const q = evSearch.toLowerCase();
const title = `${ev.title_zh||ev.title||""} ${ev.teacher_zh||""}`.toLowerCase();
if (!title.includes(q)) return false;
}
if (evFilter.type && ev.event_type !== evFilter.type) return false;
return true;
});

const filteredUpcoming = allFiltered.filter(ev => {
  if (!ev.start_date) return true;
  const end = ev.end_date || ev.start_date;
  if (end < today) return false;
  if (evTab === "teaching") return ev.event_category === "teaching";
  if (evTab === "group_practice") return ev.event_category === "group_practice";
  if (evTab === "normal") return ev.event_category === "normal";
  return true;
}).slice(0, 20);

const filteredPast = allFiltered.filter(ev => {
  if (!ev.start_date) return false;
  const end = ev.end_date || ev.start_date;
  return end < today;
});

// ── Inline auth form (kept for scroll-to, hidden by default — shown when user clicks CTA) ──
const AuthInline = () => {
const [email,setEmail]=useState("");
const [pwd,setPwd]=useState("");
const [name,setName]=useState("");
const [mode,setMode]=useState("login");
const [msg,setMsg]=useState("");
const [loading,setLoading]=useState(false);

const inputStyle = {
width:"100%",padding:"10px 13px",borderRadius:8,
border:`1.5px solid rgba(212,175,55,0.5)`,
background:"#fff",color:TXT,fontSize:14,
boxSizing:"border-box",outline:"none",
};
const labelStyle = {fontSize:11,fontWeight:700,color:TXT,letterSpacing:1.5,marginBottom:5,display:"block"};

const handleSubmit=async(e)=>{
e.preventDefault();setLoading(true);setMsg("");
try{
const r=await fetch(`${API}/vajra/${mode==="register"?"register":"login"}`,{
method:"POST",headers:{"Content-Type":"application/json"},
body:JSON.stringify(mode==="register"?{email,name,password:pwd}:{email,password:pwd}),
});
const d=await r.json();
if(r.ok&&d.token){
localStorage.setItem("vajra_token",d.token);
if(onLogin) onLogin(d.member||d, d.token);
setShowAuthModal(false);
}else{setMsg(d.detail||(lang==="zh"?"登录失败":"Login failed"));}
}catch{setMsg(lang==="zh"?"网络错误":"Network error");}
setLoading(false);
};

return (
<div onClick={()=>setShowAuthModal(false)} style={{position:"fixed",inset:0,zIndex:3000,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}>
<div onClick={e=>e.stopPropagation()} style={{background:BG,borderRadius:16,padding:"32px 36px",maxWidth:420,width:"90%",boxShadow:"0 8px 40px rgba(107,79,16,0.2)",border:"1px solid rgba(212,175,55,0.2)"}}>
<button onClick={()=>setShowAuthModal(false)} style={{position:"absolute",top:12,right:16,background:"none",border:"none",fontSize:18,color:TXT2,cursor:"pointer"}}>✕</button>
<div style={{textAlign:"center",marginBottom:20}}>
<div style={{fontSize:14,color:GD,letterSpacing:2}}>
{L("进入如意宝金刚社群","Enter the Vajra Sangha")}
</div>
</div>
<div style={{display:"flex",justifyContent:"center",gap:0,marginBottom:24}}>
<button onClick={()=>setMode("login")} style={{
padding:"8px 24px",border:`1px solid ${mode==="login"?GD:"rgba(212,175,55,0.3)"}`,
borderRadius:"8px 0 0 8px",background:mode==="login"?GD:"transparent",
color:mode==="login"?"#fff":GD,fontSize:13,cursor:"pointer",letterSpacing:1}}>
{L("登录","Login")}
</button>
<button onClick={()=>setMode("register")} style={{
padding:"8px 24px",border:`1px solid ${mode==="register"?GD:"rgba(212,175,55,0.3)"}`,
borderRadius:"0 8px 8px 0",borderLeft:"none",
background:mode==="register"?GD:"transparent",
color:mode==="register"?"#fff":GD,fontSize:13,cursor:"pointer",letterSpacing:1}}>
{L("注册","Register")}
</button>
</div>
<form onSubmit={handleSubmit}>
{mode==="register"&&<div style={{marginBottom:12}}><label style={labelStyle}>{L("姓名","Name")}</label>
<input value={name} onChange={e=>setName(e.target.value)} style={inputStyle} required/></div>}
<div style={{marginBottom:12}}><label style={labelStyle}>{L("邮箱","Email")}</label>
<input type="email" value={email} onChange={e=>setEmail(e.target.value)} style={inputStyle} required/></div>
<div style={{marginBottom:16}}><label style={labelStyle}>{L("密码","Password")}</label>
<input type="password" value={pwd} onChange={e=>setPwd(e.target.value)} style={inputStyle} required minLength={6}/></div>
{msg&&<div style={{fontSize:12,color:"#dc2626",marginBottom:12,textAlign:"center"}}>{msg}</div>}
<button type="submit" disabled={loading} style={{
width:"100%",padding:"12px",background:`linear-gradient(135deg,${GD},#A07828)`,
border:"none",borderRadius:8,color:"#fff",fontSize:14,cursor:"pointer",letterSpacing:2}}>
{loading?L("处理中…","Processing…"):mode==="login"?L("登录","Login"):L("注册加入","Register")}
</button>
</form>
</div>
</div>
);
};

return (
<div style={{minHeight:"100vh",background:BG,fontFamily:"'Noto Serif SC','Noto Serif',serif",color:TXT}}>

{/* ── Kailash Hero ── */}
<div style={{position:"relative",overflow:"hidden"}}>
<div style={{
width:"100%",height:"min(72vw,560px)",
backgroundImage:"url('/hero.jpg')",
backgroundSize:"cover",backgroundPosition:"center 25%",
filter:"brightness(1.08) saturate(1.18) contrast(1.04)",
}}/>
{/* Overlay: darker gradient so white text stays readable on bright mountain.
    Progressively darkens bottom 40% where all text lives. */}
<div style={{
position:"absolute",top:0,left:0,right:0,bottom:0,
background:"linear-gradient(180deg,rgba(0,0,0,0.15) 0%,transparent 40%,rgba(0,0,0,0.35) 70%,rgba(0,0,0,0.50) 100%)",
zIndex:1,
}}/>
{/* Golden horizon bloom — warmth */}
<div style={{position:"absolute",inset:0,zIndex:2,
background:"radial-gradient(ellipse 85% 46% at 50% 60%,rgba(230,180,40,.18) 0%,rgba(200,130,20,.06) 52%,transparent 70%)",
pointerEvents:"none"}}/>
<div style={{position:"absolute",bottom:0,left:0,right:0,textAlign:"center",padding:"0 24px 44px",zIndex:2}}>
<div style={{fontSize:"clamp(10px,1.8vw,12px)",color:"rgba(255,245,210,0.82)",letterSpacing:4,marginBottom:14,
textTransform:"uppercase",fontWeight:500,
textShadow:"0 1px 4px rgba(0,0,0,0.7)"}}>
{L("显密文教基金会 · 加拿大 · Buddhist Terma Foundation Canada",
"Buddhist Terma Foundation Canada · 显密文教基金会")}
</div>
<div style={{fontSize:"clamp(40px,8vw,64px)",color:"#FFFFFF",letterSpacing:"0.15em",marginBottom:10,
fontFamily:"'Noto Serif SC',serif",fontWeight:600,
textShadow:"0 0 50px rgba(251,191,36,0.55),0 0 30px rgba(245,158,11,0.7),0 0 15px rgba(255,255,255,0.9),2px 2px 12px rgba(0,0,0,0.85)",
animation:"vajraTitleBreath 4s ease-in-out infinite"}}>
{L("如意宝金刚社群","BTF Canada Vajra Community")}
</div>
<div style={{marginBottom:20,fontSize:"clamp(13px,2.2vw,16px)",color:"rgba(255,255,255,0.88)",letterSpacing:2,
lineHeight:1.7,textShadow:"1px 1px 6px rgba(0,0,0,0.8)"}}>
{L("金刚乘行者家园 · 各宗和合","Vajrayana Practitioner Home · All Lineages")}
</div>
{member && (
<div style={{fontSize:"clamp(13px,2vw,15px)",color:"rgba(255,235,180,0.95)",letterSpacing:2,
fontFamily:"'Noto Serif SC',serif",textShadow:"0 1px 4px rgba(0,0,0,0.7)"}}>
{(member.dharma_name||member.name||L("师兄","Vajra sibling"))}{L("，吉祥如意",", Tashi Delek")}
</div>
)}
</div>
{/* Scroll-down arrow */}
<div style={{position:"absolute",bottom:8,left:"50%",transform:"translateX(-50%)",zIndex:3}}>
<div style={{
  animation:"vajraBounce 2s ease-in-out infinite",
  color:GD,fontSize:20,opacity:0.7,cursor:"pointer",
}}
onClick={()=>{const el=document.getElementById("vajra-featured");if(el)el.scrollIntoView({behavior:"smooth"});}}
>↓</div>
</div>
<style>{`
@keyframes vajraBounce {
  0%, 100% { transform: translateY(0); opacity: 0.7; }
  50% { transform: translateY(8px); opacity: 0.4; }
}
@keyframes vajraTitleBreath {
  0%, 100% { textShadow: "0 0 50px rgba(251,191,36,0.55),0 0 30px rgba(245,158,11,0.7),0 0 15px rgba(255,255,255,0.9),2px 2px 12px rgba(0,0,0,0.85)"; }
  50% { textShadow: "0 0 65px rgba(251,191,36,0.75),0 0 42px rgba(245,158,11,0.9),0 0 22px rgba(255,255,255,1),2px 2px 14px rgba(0,0,0,0.9)"; }
}
/* Mobile responsive */
@media (max-width: 768px) {
  .vajra-hero { height: min(100vw, 360px) !important; }
  .vajra-event-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
  .vajra-event-card { padding: 16px !important; }
  .vajra-event-title { font-size: 16px !important; }
  .vajra-past-grid { grid-template-columns: 1fr !important; }
  .vajra-footer-grid { grid-template-columns: 1fr 1fr !important; }
  .vajra-section-header { padding: "24px 16px 12px" !important; }
}
@media (max-width: 480px) {
  .vajra-footer-grid { grid-template-columns: 1fr !important; }
}
`}</style>
</div>

{/* ── Phase 118: Energy-matched recommendations (logged-in only) ── */}
{member && recommendedEvents && recommendedEvents.length > 0 && (
  <div style={{maxWidth:1080,margin:"0 auto",padding:"16px 24px"}}>
    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
      <div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,rgba(184,134,11,0.3))"}}/>
      <div style={{fontSize:12,color:GD,letterSpacing:2,fontWeight:500}}>
        {L("为你推荐","Recommended for You")}
      </div>
      <div style={{height:1,flex:1,background:"linear-gradient(90deg,rgba(184,134,11,0.3),transparent)"}}/>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
      {recommendedEvents.slice(0,3).map(ev => (
        <div key={ev.id} onClick={()=>setSelectedEvent(ev)} style={{
          cursor:"pointer",padding:"14px 18px",borderRadius:10,
          background:"rgba(212,175,55,0.04)",border:"1px solid rgba(212,175,55,0.2)",
          transition:"transform .2s",
        }}
        onMouseOver={e=>e.currentTarget.style.transform="translateY(-2px)"}
        onMouseOut={e=>e.currentTarget.style.transform="none"}>
          <div style={{fontSize:13,fontWeight:600,color:TXT,marginBottom:4}}>
            {lang === 'en' && ev.title_en ? ev.title_en : ev.title_zh}
          </div>
          <div style={{fontSize:11,color:TXT2}}>
            📅 {ev.start_date} · {ev.energy_direction}
          </div>
        </div>
      ))}
    </div>
  </div>
)}

{/* ── Section 2: Important Events Banner ── */}
{featuredSeries.length > 0 && (
  <div style={{background:BG, padding:"24px 0 0"}}>
    <div style={{maxWidth:1080,margin:"0 auto",padding:"0 24px"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
        <div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,rgba(184,134,11,0.3))"}}/>
        <div style={{fontSize:12,color:GD,letterSpacing:2,textTransform:"uppercase",fontWeight:500}}>
          {L("重要法讯","Important Events")}
        </div>
        <div style={{height:1,flex:1,background:"linear-gradient(90deg,rgba(184,134,11,0.3),transparent)"}}/>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {featuredSeries.map(s => {
          if (s.slug === "cittamani-tara-2026") return null;  // shown in carousel instead
          if (!s.slug) return null;  // skip series without slug
          const isPast = s.end_date ? new Date() > new Date(s.end_date + "T23:59:59-04:00") : false;
          if (isPast) return null;
          const tc = s.theme_color || "indigo-gold";
          const t = { "red-gold": ["#6b1a1a","#4a0f0f","#d4a84b"], "sakya": ["#1e3a5f","#b91c1c","#f0c040"], "indigo-gold": ["#1a2744","#0f1d35","#d4a84b"] }[tc] || ["#1a2744","#0f1d35","#d4a84b"];
          const [bg1, bg2, accent] = t;
          const isLight = false; // sakya also dark now
          return (
            <div key={s.id} onClick={() => {
              navigate(`/vajra/special/${s.slug}`);
            }} style={{
              cursor: "pointer", background: `linear-gradient(135deg, ${bg1} 0%, ${bg2} 40%, ${bg1} 100%)`,
              borderRadius: 14, padding: "16px 24px", border: `1px solid ${isLight ? "rgba(30,58,95,0.3)" : "rgba(212,168,75,0.25)"}`,
              display: "flex", alignItems: "center", gap: 16, transition: "box-shadow .2s",
            }} onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 24px ${isLight ? "rgba(30,58,95,0.15)" : "rgba(26,39,68,0.25)"}`}
               onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
              <div style={{ minWidth: 44, height: 44, borderRadius: "50%", background: `rgba(${isLight ? "30,58,95" : "212,168,75"},0.15)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>👑</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: isLight ? "#1e3a5f" : accent, letterSpacing: 1, marginBottom: 2 }}>{s.title_zh}</div>
                <div style={{ fontSize: 11, color: isLight ? "rgba(26,26,46,0.6)" : "rgba(255,255,255,0.6)" }}>
                  {s.start_date}{s.end_date && s.end_date !== s.start_date ? ` – ${s.end_date}` : ""}{s.teacher_zh ? ` · ${s.teacher_zh}` : ""} →
                </div>
              </div>
              <div style={{ fontSize: 18, color: isLight ? "rgba(26,26,46,0.3)" : "rgba(255,255,255,0.3)" }}>→</div>
            </div>
          );
        }).filter(Boolean)}
      </div>
    </div>
  </div>
)}

{/* ── Section 3: Featured Carousel ── */}
<div id="vajra-featured" style={{background:BG, paddingBottom:16}}>
  <FeaturedCarousel featuredEvents={carouselEvents} lang={lang} />
</div>

<div style={{maxWidth:1080,margin:"0 auto",padding:"0 24px 80px"}}>

{/* ── Auth modal (shown on demand) ── */}
{showAuthModal && <AuthInline />}

{/* ── Section 4: 近期活动 (Upcoming Activities — tabbed) ── */}
<div style={{marginBottom:60}}>
<div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
<div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,rgba(184,134,11,0.3))"}}/>
<div style={{fontSize:12,color:GD,letterSpacing:2,textTransform:"uppercase",fontWeight:500}}>
{L("近期活动","Upcoming Activities")}
</div>
<a href="/vajra/calendar" style={{fontSize:11,color:GD,textDecoration:"none",letterSpacing:.5,marginLeft:8}}>
{L("完整日历 →","Full Calendar →")}
</a>
<div style={{height:1,flex:1,background:"linear-gradient(90deg,rgba(184,134,11,0.3),transparent)"}}/>
</div>

{/* Search row */}
<div style={{marginBottom:16,display:"flex",flexWrap:"wrap",gap:8,alignItems:"center"}}>
<input
value={evSearch} onChange={e=>setEvSearch(e.target.value)}
placeholder={L("搜索法会 / 上师…","Search events / teachers…")}
style={{padding:"6px 12px",borderRadius:20,border:"1px solid rgba(139,105,20,0.3)",
background:"#fff",color:TXT,fontSize:12,outline:"none",minWidth:180}}/>
</div>

{/* Tab row */}
<div style={{display:"flex",gap:0,marginBottom:20,borderBottom:"1px solid rgba(212,175,55,0.2)"}}>
{[
{key:"normal", zh:"法讯", en:"Public Events"},
{key:"teaching", zh:"教法", en:"Teachings"},
{key:"group_practice", zh:"共修", en:"Practices"},
].map(tab => (
<button key={tab.key} onClick={()=>setEvTab(tab.key)} style={{
padding:"8px 20px",fontSize:12,cursor:"pointer",
background:"transparent",border:"none",
borderBottom:`2px solid ${evTab===tab.key?GDH:"transparent"}`,
color:evTab===tab.key?GD:TXT2,
transition:"all .15s",letterSpacing:.5,
}}>
{L(tab.zh, tab.en)}
</button>
))}
</div>

{filteredUpcoming.length === 0 ? (
<div style={{textAlign:"center",padding:"40px 0",color:TXT2,fontSize:13,letterSpacing:.5}}>
{evSearch||evFilter.type||true
? L("没有符合条件的法讯","No events match your filter")
: L("暂无近期法讯 · 敬请关注","No upcoming events · Please check back soon")}
<div style={{marginTop:8}}>
<a href="/vajra/calendar" style={{color:GD,fontSize:12,textDecoration:"none"}}>
{L("查看往期回顾 →","View past events →")}
</a>
</div>
</div>
) : (
<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:20}}>
{filteredUpcoming.map(ev=>{
const daysUntil = getDaysUntil(ev.start_date);
const isFree = !ev.fee_amount || ev.donation_type === "free";
const isExternal = ev.is_external;
const karma = getEventKarmaStyle(ev);
return (
<div key={ev.id} onClick={()=>setSelectedEvent(ev)} style={{
position:"relative",
background: karma.bg,
border:`1px solid ${karma.border}`,
borderRadius:12,padding:24,
backdropFilter:"blur(8px)",
boxShadow:"0 1px 3px rgba(0,0,0,0.04)",
transition:"transform .25s,box-shadow .25s,border-color .25s",cursor:"pointer",
display:"flex",flexDirection:"column",overflow:"hidden",
}}
onMouseOver={e=>{e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.boxShadow=karma.shadow;e.currentTarget.style.borderColor=karma.borderHover;}}
onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,0.04)";e.currentTarget.style.borderColor=karma.border;}}>
{/* DZOKDEN 左侧能量指示条 */}
<div style={{position:"absolute",left:0,top:0,bottom:0,width:3,background:karma.indicator,borderRadius:"12px 0 0 12px"}}/>
{ev.cover_image_url && (
<div style={{margin:"-24px -24px 16px -24px",height:160,overflow:"hidden"}}>
<img src={ev.cover_image_url} alt=""
style={{width:"100%",height:"100%",objectFit:"cover"}}/>
</div>
)}
<div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
<span style={{
fontSize:10,letterSpacing:1.5,padding:"3px 10px",borderRadius:10,
background:`${EVENT_TYPE_COLOR[ev.event_type]||"#555"}18`,
color:EVENT_TYPE_COLOR[ev.event_type]||GD,
border:`1px solid ${EVENT_TYPE_COLOR[ev.event_type]||"#555"}44`,
}}>{EVENT_TYPE_LABEL[ev.event_type]||ev.event_type}</span>
{ev.start_date&&<span style={{fontSize:11,color:TXT2,letterSpacing:.5}}>
{ev.start_date}{ev.start_time?` · ${ev.start_time} ${TZ_LABELS[ev.timezone]||""}` :""}
</span>}
</div>
<div style={{fontSize:17,color:TXT,letterSpacing:.5,lineHeight:1.5,marginBottom:8}}>
{(lang === 'en' && ev.title_en ? ev.title_en : ev.title_zh)||ev.title||L("法会活动","Dharma Event")}
</div>
{ev.teacher_zh&&<div style={{fontSize:12,color:GD,marginBottom:6}}>☸ {lang === 'en' && ev.teacher_en ? ev.teacher_en : ev.teacher_zh}</div>}
{ev.location&&<div style={{fontSize:11,color:TXT2,marginBottom:8}}>📍 {ev.location}</div>}
<div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
{ev.capacity&&ev.capacity>0&&(
<span style={{fontSize:10,padding:"2px 8px",borderRadius:8,
background:"rgba(139,105,20,0.07)",color:GDL,border:"1px solid rgba(139,105,20,0.18)"}}>
🔥 {L(`仅剩 ${ev.capacity} 名额`,`${ev.capacity} spots left`)}
</span>
)}
{!isFree&&ev.fee_amount&&(
<span style={{fontSize:10,padding:"2px 8px",borderRadius:8,
background:"rgba(139,105,20,0.07)",color:GDL,border:"1px solid rgba(139,105,20,0.2)"}}>
💰 {L(`建议供养 $${ev.fee_amount}`,`Suggested offering $${ev.fee_amount}`)}
</span>
)}
{isFree&&(
<span style={{fontSize:10,padding:"2px 8px",borderRadius:8,
background:"rgba(6,78,59,0.07)",color:"#065f46",border:"1px solid rgba(6,78,59,0.2)"}}>
{L("免费开放","Free & Open")}
</span>
)}
{ev.event_type==="empowerment"&&(
<span style={{fontSize:10,padding:"2px 8px",borderRadius:8,
background:"rgba(139,105,20,0.07)",color:GDL,border:"1px solid rgba(139,105,20,0.18)"}}>
{L("需灌顶资格","Initiation required")}
</span>
)}
{daysUntil&&daysUntil<=30&&(()=>{
const urgent = daysUntil <= 2;
const soon = daysUntil <= 7;
const icon = urgent ? "🔥" : soon ? "⏰" : "📅";
const bg = urgent ? "rgba(239,68,68,0.09)" : soon ? "rgba(245,158,11,0.09)" : "rgba(139,105,20,0.07)";
const fg = urgent ? "#dc2626" : soon ? "#d97706" : GDL;
const bd = urgent ? "rgba(239,68,68,0.25)" : soon ? "rgba(245,158,11,0.25)" : "rgba(139,105,20,0.18)";
return (
<span style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:bg,color:fg,border:`1px solid ${bd}`}}>
{icon} {L(`${daysUntil}天后开始`,`Starts in ${daysUntil} days`)}
</span>
);})()}
{/* 🌐 世界法讯 tag for external events */}
{isExternal && (
<span style={{fontSize:10,padding:"2px 8px",borderRadius:8,
background:"rgba(30,64,175,0.07)",color:"#1e40af",border:"1px solid rgba(30,64,175,0.2)"}}>
🌐 {L("世界法讯","Global")}
</span>
)}
</div>
{ev.spiritual_function&&<div style={{fontSize:11,color:"rgba(184,134,11,0.6)",letterSpacing:.5,marginBottom:10}}>
{ev.spiritual_function}
</div>}
<div style={{display:"flex",gap:10,alignItems:"center",marginTop:"auto",paddingTop:12}}>
{ev.registration_required&&(
<button onClick={e=>{e.stopPropagation();setShowAuthModal(true);}} style={{
padding:"9px 24px",background:`linear-gradient(135deg,${GD},#A07828)`,
border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:600,
cursor:"pointer",letterSpacing:1,
boxShadow:"0 3px 12px rgba(107,79,16,0.25)",
transition:"transform .15s,box-shadow .15s",
}}
onMouseOver={e=>{e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.boxShadow="0 5px 18px rgba(107,79,16,0.35)";}}
onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="0 3px 12px rgba(107,79,16,0.25)";}}>
{L("登录报名","Login to Register")}
</button>
)}
<button onClick={e=>{e.stopPropagation();setSelectedEvent(ev);}} style={{
padding:"7px 16px",whiteSpace:"nowrap",
background:"transparent",border:"1px solid rgba(139,105,20,.20)",
borderRadius:7,color:GDL,fontSize:11,cursor:"pointer",letterSpacing:.5,
transition:"all .2s",
}}
onMouseOver={e=>{e.currentTarget.style.background="rgba(139,105,20,.06)";e.currentTarget.style.borderColor="rgba(139,105,20,.35)";e.currentTarget.style.color=GD;}}
onMouseOut={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor="rgba(139,105,20,.20)";e.currentTarget.style.color=GDL;}}>
{L("查看详情 →","View Details →")}
</button>
</div>
</div>
);
})}
</div>
)}
</div>

{/* ── 法讯日历 ── */}
<div id="vajra-calendar" style={{marginBottom:60}}>
<div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
<div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,rgba(184,134,11,0.3))"}}/>
<div style={{fontSize:12,color:GD,letterSpacing:2,textTransform:"uppercase",fontWeight:500}}>
{L("法讯日历","Dharma Calendar")}
</div>
<div style={{height:1,flex:1,background:"linear-gradient(90deg,rgba(184,134,11,0.3),transparent)"}}/>
</div>
<PublicEventCalendar events={allEvents} calMonth={calMonth} setCalMonth={setCalMonth} setSelectedEvent={setSelectedEvent}/>
</div>

{/* ── 往期法会 ── */}
{filteredPast.length > 0 && (
<div style={{marginBottom:60}}>
<div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
<div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,rgba(184,134,11,0.3))"}}/>
<div style={{fontSize:12,color:GD,letterSpacing:2,textTransform:"uppercase",fontWeight:500}}>
{L("往期法会","Past Events")}
</div>
<div style={{height:1,flex:1,background:"linear-gradient(90deg,rgba(184,134,11,0.3),transparent)"}}/>
</div>
<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
{(pastExpanded ? filteredPast : filteredPast.slice(0, 6)).map(ev=>{
let galleryImgs = ev.gallery_images;
if (typeof galleryImgs === 'string') { try { galleryImgs = JSON.parse(galleryImgs); } catch(e) { galleryImgs = null; } }
if (!Array.isArray(galleryImgs)) galleryImgs = null;
const pkarma = getEventKarmaStyle(ev);
return (
<div key={ev.id} onClick={()=>navigate(`/events/${ev.slug}`)} style={{
position:"relative",
background: pkarma.bg,
border:`1px solid ${pkarma.border}`,
borderRadius:12,padding:"18px 20px",
backdropFilter:"blur(6px)",
cursor:"pointer",transition:"transform .2s,box-shadow .2s,border-color .2s",
}}
onMouseOver={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow=pkarma.shadow;e.currentTarget.style.borderColor=pkarma.borderHover;}}
onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";e.currentTarget.style.borderColor=pkarma.border;}}>
{/* DZOKDEN 左侧能量指示条 */}
<div style={{position:"absolute",left:0,top:0,bottom:0,width:3,background:pkarma.indicator,borderRadius:"12px 0 0 12px"}}/>
<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
<span style={{
fontSize:10,letterSpacing:1.5,padding:"2px 8px",borderRadius:8,
background:`${(EVENT_TYPE_COLOR[ev.event_type]||"#555")}15`,
color:EVENT_TYPE_COLOR[ev.event_type]||GD,
border:`1px solid ${(EVENT_TYPE_COLOR[ev.event_type]||"#555")}33`,
}}>{EVENT_TYPE_LABEL[ev.event_type]||ev.event_type}</span>
{ev.start_date&&<span style={{fontSize:10,color:TXT2,opacity:.7}}>
📅 {ev.start_date}{ev.end_date&&ev.end_date!==ev.start_date?` — ${ev.end_date}`:""}
</span>}
</div>
<div style={{fontSize:14,color:TXT,letterSpacing:.5,lineHeight:1.4,marginBottom:6}}>
{(lang === 'en' && ev.title_en ? ev.title_en : ev.title_zh)||ev.title||L("法会活动","Dharma Event")}
</div>
{ev.teacher_zh&&<div style={{fontSize:11,color:GD,marginBottom:4,opacity:.7}}>☸ {lang === 'en' && ev.teacher_en ? ev.teacher_en : ev.teacher_zh}</div>}
{ev.location&&<div style={{fontSize:10,color:TXT2,opacity:.7}}>📍 {ev.location}</div>}
{(()=>{const desc=lang==='en'?(ev.description_en||ev.description_zh):(ev.description_zh||ev.description_en);if(!desc)return null;return <div style={{fontSize:10,color:"rgba(0,0,0,0.45)",marginTop:6,lineHeight:1.5}}>{desc}</div>;})()}
{galleryImgs && galleryImgs.length > 0 && (
<div style={{display:"flex",gap:6,marginTop:10,overflow:"hidden"}}>
{galleryImgs.slice(0,4).map((url,i)=>(
<div key={i} style={{width:56,height:56,borderRadius:6,overflow:"hidden",flexShrink:0,
background:`url(${url}) center/cover`,border:"1px solid rgba(0,0,0,0.08)"}}/>
))}
{galleryImgs.length > 4 && (
<div style={{width:56,height:56,borderRadius:6,background:"rgba(0,0,0,0.04)",display:"flex",alignItems:"center",justifyContent:"center",
fontSize:11,color:"#999",flexShrink:0,border:"1px solid rgba(0,0,0,0.06)"}}>
+{galleryImgs.length - 4}
</div>
)}
</div>
)}
</div>
);
})}
</div>
{filteredPast.length > 6 && (
  <div onClick={() => setPastExpanded(!pastExpanded)} style={{
    textAlign: "center", cursor: "pointer", marginTop: 16,
    padding: "8px 0", borderRadius: 8, fontSize: 12, color: GD,
    background: "rgba(212,175,55,0.06)", border: "1px dashed rgba(212,175,55,0.25)",
  }}>{pastExpanded ? L("收起 ▲", "Collapse ▲") : L(`显示全部 ${filteredPast.length} 个往期活动 ↓`, `Show all ${filteredPast.length} past events ↓`)}</div>
)}
<div style={{textAlign:"center",marginTop:20,display:"flex",justifyContent:"center",gap:24,flexWrap:"wrap"}}>
<a href="/vajra/calendar" style={{fontSize:11,color:GD,textDecoration:"none",letterSpacing:.5,opacity:.7}}>
{L("查看完整日历 →","View full calendar →")}
</a>
<a href="/vajra/gallery" style={{fontSize:11,color:GD,textDecoration:"none",letterSpacing:.5,opacity:.7}}>
📸 {L("往期照片回顾 →","Photo Gallery →")}
</a>
</div>
</div>
)}

</div>

{/* ── 圆满功德榜 ── */}
<div style={{maxWidth:1080,margin:"0 auto",padding:"0 24px 40px"}}>
  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
    <div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,rgba(184,134,11,0.3))"}}/>
    <div style={{fontSize:12,color:GD,letterSpacing:2,fontWeight:500}}>圆满功德</div>
    <div style={{height:1,flex:1,background:"linear-gradient(90deg,rgba(184,134,11,0.3),transparent)"}}/>
  </div>
  <MeritBoard maxItems={3} />
</div>

{/* ── Section 5: Our Teachers ── */}
{teachers.length > 0 && (
  <div style={{maxWidth:1080,margin:"0 auto",padding:"0 24px 60px"}}>
    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
      <div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,rgba(184,134,11,0.3))"}}/>
      <div style={{fontSize:12,color:GD,letterSpacing:2,textTransform:"uppercase",fontWeight:500}}>
        {L("传承上师","Our Teachers")}
      </div>
      <a href="/vajra/teachers" style={{fontSize:11,color:GD,textDecoration:"none",letterSpacing:.5,marginLeft:8}}>
        {L("全部上师 →","All Teachers →")}
      </a>
      <div style={{height:1,flex:1,background:"linear-gradient(90deg,rgba(184,134,11,0.3),transparent)"}}/>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:16}}>
      {teachers.slice(0,6).map(t => (
        <div key={t.id} onClick={()=>navigate(`/vajra/teacher/${t.slug||t.id}`)} style={{
          cursor:"pointer",borderRadius:12,padding:20,
          background:"rgba(212,175,55,0.04)",
          border:"1px solid rgba(212,175,55,0.18)",
          textAlign:"center",transition:"transform .2s,box-shadow .2s",
        }}
        onMouseOver={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 4px 20px rgba(212,175,55,0.15)";}}
        onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
          {t.photo_url ? (
            <img src={t.photo_url} alt={t.name_zh} style={{width:72,height:72,borderRadius:"50%",objectFit:"cover",border:`2px solid rgba(212,175,55,0.3)`,marginBottom:10}}/>
          ) : (
            <div style={{width:72,height:72,borderRadius:"50%",background:"rgba(212,175,55,0.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 10px"}}>☸</div>
          )}
          <div style={{fontSize:14,color:TXT,fontWeight:500,marginBottom:4}}>{t.name_zh}</div>
          {t.tradition && <div style={{fontSize:11,color:GD,opacity:.7,letterSpacing:.5}}>{t.tradition}</div>}
        </div>
      ))}
    </div>
  </div>
)}

{/* ── Section 6: Support & Services ── */}
<div style={{background:"rgba(212,175,55,0.04)",borderTop:"1px solid rgba(212,175,55,0.15)",padding:"48px 24px"}}>
  <div style={{maxWidth:1080,margin:"0 auto"}}>
    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:32}}>
      <div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,rgba(184,134,11,0.3))"}}/>
      <div style={{fontSize:12,color:GD,letterSpacing:2,textTransform:"uppercase",fontWeight:500}}>
        {L("供养与服务","Support & Services")}
      </div>
      <div style={{height:1,flex:1,background:"linear-gradient(90deg,rgba(184,134,11,0.3),transparent)"}}/>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:20}}>
      {[
        {icon:"🪔",title:L("供养三宝","Make Offerings"),desc:L("以布施积聚资粮","Accumulate merit through generosity"),href:"/vajra/offerings"},
        {icon:"🙏",title:L("祈请法事","Dharma Services"),desc:L("祈请法会与修法服务","Request pujas and practice services"),href:"/vajra/service"},
        {icon:"📿",title:L("法宝流通","Dharma Shop"),desc:L("免费结缘法宝","Free dharma items for practitioners"),href:"/vajra/shop"},
        {icon:"📧",title:L("联系我们","Contact Us"),desc:L("与BTF加拿大社群联系","Connect with the BTF Canada community"),href:"mailto:canada.btf@gmail.com"},
      ].map((item,i) => (
        <a key={i} href={item.href} style={{
          display:"block",textDecoration:"none",
          borderRadius:12,padding:"20px 18px",
          background:"rgba(255,255,255,0.7)",
          border:"1px solid rgba(212,175,55,0.2)",
          transition:"transform .2s,box-shadow .2s",
        }}
        onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 4px 16px rgba(212,175,55,0.12)";}}
        onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
          <div style={{fontSize:28,marginBottom:10}}>{item.icon}</div>
          <div style={{fontSize:13,color:GD,fontWeight:600,marginBottom:6,letterSpacing:.5}}>{item.title}</div>
          <div style={{fontSize:12,color:TXT2,lineHeight:1.6}}>{item.desc}</div>
        </a>
      ))}
    </div>
  </div>
</div>

{/* ── 4-column Footer ── */}
<footer style={{
background:"rgba(45,24,16,0.04)",borderTop:"1px solid rgba(212,175,55,0.2)",
padding:"48px 32px 28px",marginTop:8,
}}>
<div style={{maxWidth:1080,margin:"0 auto"}}>
<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:32,marginBottom:36}}>
<div>
<div style={{fontSize:12,color:GD,letterSpacing:1.5,fontWeight:600,marginBottom:14,textTransform:"uppercase"}}>
{L("快速导航","Quick Links")}
</div>
{[
{label:L("传统占察","Traditional Zhànchá"), href:"/oracle"},
{label:L("近期法讯","Upcoming Events"), href:"/vajra/calendar"},
{label:L("传承上师","Lineage Teachers"), href:"/vajra/teachers"},
{label:L("教法图书馆","Dharma Library"), href:"/vajra"},
{label:L("法宝流通","Dharma Shop"), href:"/vajra/shop"},
{label:L("寺院供养","Temple Offerings"), href:"/vajra/temples"},
{label:L("修行次第","Practice Path"), href:"/vajra/my-path"},
].map(l=>(
<a key={l.href+l.label} href={l.href} style={{display:"block",fontSize:13,color:TXT2,
textDecoration:"none",marginBottom:8,letterSpacing:.3}}>
{l.label}
</a>
))}
</div>
<div>
<div style={{fontSize:12,color:GD,letterSpacing:1.5,fontWeight:600,marginBottom:14,textTransform:"uppercase"}}>
{L("联系我们","Contact Us")}
</div>
{[
{icon:"📧",text:"canada.btf@gmail.com",href:"mailto:canada.btf@gmail.com"},
{icon:"💬",text:"WeChat: jxuabc",href:null},
].map((c,i)=>(
<div key={i} style={{display:"flex",gap:7,alignItems:"flex-start",marginBottom:9,fontSize:12,color:TXT2}}>
<span style={{fontSize:13}}>{c.icon}</span>
{c.href
? <a href={c.href} style={{color:TXT2,textDecoration:"none"}}>{c.text}</a>
: <span>{c.text}</span>}
</div>
))}
</div>
<div>
<div style={{fontSize:12,color:GD,letterSpacing:1.5,fontWeight:600,marginBottom:14,textTransform:"uppercase"}}>
{L("法律","Legal")}
</div>
{[
{label:L("隐私政策","Privacy Policy"), href:"/privacy"},
{label:L("服务条款","Terms of Service"), href:"/terms"},
].map(l=>(
<a key={l.href} href={l.href} style={{display:"block",fontSize:13,color:TXT2,
textDecoration:"none",marginBottom:8,letterSpacing:.3}}>
{l.label}
</a>
))}
</div>
<div>
<div style={{fontSize:12,color:GD,letterSpacing:1.5,fontWeight:600,marginBottom:14,textTransform:"uppercase"}}>
{L("传承共修","Lineage Practices")}
</div>
<div style={{fontSize:12,color:TXT2,lineHeight:2,letterSpacing:.3}}>
<div>格鲁 · 如意珠度母 · 大威德金刚</div>
<div>宁玛 · 普巴金刚 · 宇拓宁提 · 大圆满 · 施身法</div>
<div>噶举 · 金刚亥母 · 那若六法</div>
<div>觉囊 · 时轮金刚</div>
</div>
</div>
</div>
<div style={{borderTop:"1px solid rgba(212,175,55,0.15)",paddingTop:18,textAlign:"center"}}>
<div style={{fontSize:12,color:TXT2,opacity:.7,letterSpacing:.5,marginBottom:6}}>
© 2026 BTF CANADA · {L("显密文教基金会 · 如意宝金刚社群 · Toronto",
"Buddhist Terma Foundation Canada · BTF Canada Vajra Community · Toronto")}
</div>
</div>
</div>
</footer>

{selectedEvent&&<EventDetailModal event={selectedEvent} onClose={()=>setSelectedEvent(null)} lang={lang} isLoggedIn={!!member} onRegister={()=>{setSelectedEvent(null);setShowAuthModal(true);}}/>}

{/* ── Lightbox ── */}
{lightboxUrl && (
<div onClick={()=>setLightboxUrl(null)} style={{
position:"fixed",inset:0,zIndex:2000,background:"rgba(0,0,0,0.85)",
display:"flex",alignItems:"center",justifyContent:"center",
cursor:"zoom-out",padding:20
}}>
<img src={lightboxUrl} alt="" style={{
maxWidth:"90vw",maxHeight:"90vh",objectFit:"contain",
borderRadius:12,boxShadow:"0 8px 32px rgba(0,0,0,0.4)"
}}/>
<button onClick={()=>setLightboxUrl(null)} style={{
position:"absolute",top:20,right:20,
background:"rgba(255,255,255,0.2)",border:"none",borderRadius:"50%",
width:40,height:40,fontSize:20,color:"#fff",cursor:"pointer",
display:"flex",alignItems:"center",justifyContent:"center"
}}>✕</button>
</div>
)}
</div>
);
}
